import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { MessageSquare, Send, Upload, Globe, FileText, Loader, RefreshCw, FolderOpen, ExternalLink } from 'lucide-react';
import api from '../services/api';
import './ChatbotPage.css';

const ChatbotPage = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [botStatus, setBotStatus] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCrawlModal, setShowCrawlModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadReplaceStore, setUploadReplaceStore] = useState(false);
  const [crawlUrl, setCrawlUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [crawling, setCrawling] = useState(false);
  const messagesEndRef = useRef(null);

  // Logs tab
  const [activeTab, setActiveTab] = useState('chat');
  const [logFiles, setLogFiles] = useState([]);
  const [selectedLogFile, setSelectedLogFile] = useState(null);
  const [logContent, setLogContent] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logContentLoading, setLogContentLoading] = useState(false);
  const [logsError, setLogsError] = useState(null);

  // SharePoint Proposals tab
  const [sharepointFiles, setSharepointFiles] = useState([]);
  const [proposalsLoading, setProposalsLoading] = useState(false);
  const [proposalsError, setProposalsError] = useState(null);

  useEffect(() => {
    fetchBotStatus();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchLogFiles = async () => {
    setLogsLoading(true);
    setLogsError(null);
    try {
      const res = await api.get('/chatbot/logs/');
      setLogFiles(res.data.files || []);
      if (!res.data.files?.length) setSelectedLogFile(null);
    } catch (err) {
      setLogsError(err.response?.data?.error || err.message || 'Failed to load logs');
      setLogFiles([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const fetchLogContent = async (filename) => {
    if (!filename) {
      setLogContent(null);
      return;
    }
    setLogContentLoading(true);
    setLogsError(null);
    try {
      const res = await api.get('/chatbot/logs/', { params: { file: filename } });
      setLogContent(res.data.content);
    } catch (err) {
      setLogsError(err.response?.data?.error || err.message || 'Failed to load file');
      setLogContent(null);
    } finally {
      setLogContentLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'logs') fetchLogFiles();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'logs' && selectedLogFile) fetchLogContent(selectedLogFile);
  }, [activeTab, selectedLogFile]);

  const fetchSharepointFiles = async () => {
    setProposalsLoading(true);
    setProposalsError(null);
    try {
      const res = await api.get('/chatbot/sharepoint-files/');
      setSharepointFiles(res.data.files || []);
    } catch (err) {
      setProposalsError(err.response?.data?.error || err.message || 'Failed to load proposals');
      setSharepointFiles([]);
    } finally {
      setProposalsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'proposals') fetchSharepointFiles();
  }, [activeTab]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchBotStatus = async () => {
    try {
      const response = await api.get('/chatbot/status/');
      console.log('Bot status response:', response.data);
      setBotStatus(response.data);
    } catch (error) {
      console.error('Error fetching bot status:', error);
      console.error('Error response:', error.response?.data);
      // Set default status if fetch fails
      setBotStatus({
        document_count: 0,
        status: 'not_started',
        has_store: false,
      });
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setLoading(true);

    // Add user message to UI immediately
    const newUserMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, newUserMessage]);

    try {
      const response = await api.post('/chatbot/chat/', {
        message: userMessage,
        session_id: sessionId,
      });

      console.log('Chat response:', response.data);
      const data = response.data;
      
      // Update session ID if new session was created
      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
      }

      // Add assistant response
      const assistantMessage = {
        role: 'assistant',
        content: data.response || 'Sorry, I could not generate a response.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = {
        role: 'assistant',
        content: error.response?.data?.error || 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
        error: true,
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!uploadFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      if (uploadReplaceStore) formData.append('replace', 'true');

      const response = await api.post('/chatbot/upload/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const data = response.data;
      alert(data.replaced
        ? `Index replaced. ${data.chunks_processed} chunks in store (old chunks erased).`
        : `File uploaded successfully! ${data.chunks_processed} chunks processed.`);
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadReplaceStore(false);
      fetchBotStatus();
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleCrawl = async () => {
    if (!crawlUrl.trim()) return;

    setCrawling(true);
    try {
      const response = await api.post('/chatbot/crawl/', {
        website_url: crawlUrl.trim(),
      });

      const data = response.data;
      alert(`Website crawled successfully! ${data.added} pages indexed.`);
      setShowCrawlModal(false);
      setCrawlUrl('');
      fetchBotStatus();
    } catch (error) {
      console.error('Error crawling website:', error);
      alert('Failed to crawl website. Please try again.');
    } finally {
      setCrawling(false);
    }
  };

  return (
    <div className="chatbot-page">
      <div className="chatbot-container">
        {/* Header */}
        <div className="chatbot-header">
          <div className="chatbot-header-content">
            <MessageSquare className="chatbot-icon" />
            <div>
              <h1>Propulse Assistant</h1>
              <p>Ask questions about your proposals and documents</p>
            </div>
          </div>
          <div className="chatbot-actions">
            <button
              className="btn-secondary"
              onClick={() => setShowUploadModal(true)}
              title="Upload file"
            >
              <Upload size={18} />
              Upload File
            </button>
            <button
              className="btn-secondary"
              onClick={() => setShowCrawlModal(true)}
              title="Crawl website"
            >
              <Globe size={18} />
              Crawl Website
            </button>
            {botStatus && (
              <div className="bot-status">
                <span className="status-indicator" data-status={botStatus.status}></span>
                <span>{botStatus.document_count || 0} documents indexed</span>
              </div>
            )}
          </div>
        </div>

        {/* Tabs: Chat | Logs */}
        <div className="chatbot-tabs">
          <button
            type="button"
            className={`chatbot-tab ${activeTab === 'chat' ? 'active' : ''}`}
            onClick={() => setActiveTab('chat')}
          >
            <MessageSquare size={18} />
            Chat
          </button>
          <button
            type="button"
            className={`chatbot-tab ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            <FileText size={18} />
            Logs
          </button>
          <button
            type="button"
            className={`chatbot-tab ${activeTab === 'proposals' ? 'active' : ''}`}
            onClick={() => setActiveTab('proposals')}
          >
            <FolderOpen size={18} />
            Proposals
          </button>
        </div>

        {/* Chat content */}
        {activeTab === 'chat' && (
        <>
        <div className="chatbot-messages">
          {messages.length === 0 && (
            <div className="welcome-message">
              <MessageSquare size={48} />
              <h2>Welcome to Propulse Assistant</h2>
              <p>I can help you find information about your proposals and documents.</p>
              <p>Try asking:</p>
              <ul>
                <li>"What proposals do we have?"</li>
                <li>"Tell me about slide 5"</li>
                <li>"What's in the marketing proposal?"</li>
              </ul>
            </div>
          )}
          
          {messages.map((msg, idx) => (
            <div key={idx} className={`message ${msg.role} ${msg.error ? 'error' : ''}`}>
              <div className="message-content">
                <ReactMarkdown className="message-markdown">{msg.content}</ReactMarkdown>
              </div>
              <div className="message-timestamp">
                {msg.timestamp?.toLocaleTimeString()}
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="message assistant">
              <div className="message-content">
                <Loader className="spinner" size={16} />
                Thinking...
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form className="chatbot-input" onSubmit={sendMessage}>
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Type your message..."
            disabled={loading}
          />
          <button type="submit" disabled={loading || !inputMessage.trim()}>
            <Send size={20} />
          </button>
        </form>
        </>
        )}

        {/* Logs tab: file list + content viewer */}
        {activeTab === 'logs' && (
          <div className="chatbot-logs-panel">
            <div className="chatbot-logs-sidebar">
              <div className="chatbot-logs-sidebar-header">
                <span>Chat log files</span>
                <button
                  type="button"
                  className="btn-icon"
                  onClick={fetchLogFiles}
                  disabled={logsLoading}
                  title="Refresh list"
                >
                  <RefreshCw size={16} />
                </button>
              </div>
              {logsError && <div className="chatbot-logs-error">{logsError}</div>}
              {logsLoading && !logFiles.length && <div className="chatbot-logs-message">Loading...</div>}
              {!logsLoading && !logFiles.length && <div className="chatbot-logs-message">No log files yet.</div>}
              <ul className="chatbot-logs-list">
                {logFiles.map((f, index) => {
                  const logDate = f.mtime ? new Date(f.mtime * 1000) : null;
                  const dateStr = logDate ? logDate.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' }) : '';
                  return (
                    <li key={f.name}>
                      <button
                        type="button"
                        className={selectedLogFile === f.name ? 'active' : ''}
                        onClick={() => setSelectedLogFile(f.name)}
                      >
                        <FileText size={14} />
                        <span className="chatbot-logs-item-content">
                          {index === 0 && <span className="chatbot-logs-badge">Latest</span>}
                          <span className="chatbot-logs-filename" title={f.name}>{f.name}</span>
                          {dateStr && <span className="chatbot-logs-date">{dateStr}</span>}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="chatbot-logs-content">
              {selectedLogFile ? (
                logContentLoading ? (
                  <div className="chatbot-logs-message">Loading...</div>
                ) : logContent ? (
                  <pre className="chatbot-logs-json">{JSON.stringify(logContent, null, 2)}</pre>
                ) : (
                  <div className="chatbot-logs-message">Select a file</div>
                )
              ) : (
                <div className="chatbot-logs-message">Select a log file to view</div>
              )}
            </div>
          </div>
        )}

        {/* Proposals tab: SharePoint folder file list */}
        {activeTab === 'proposals' && (
          <div className="chatbot-proposals-panel">
            <div className="chatbot-proposals-header">
              <span>Proposals (SharePoint)</span>
              <button
                type="button"
                className="btn-icon"
                onClick={fetchSharepointFiles}
                disabled={proposalsLoading}
                title="Refresh list"
              >
                <RefreshCw size={16} />
              </button>
            </div>
            {proposalsError && <div className="chatbot-logs-error">{proposalsError}</div>}
            {proposalsLoading && !sharepointFiles.length && <div className="chatbot-logs-message">Loading...</div>}
            {!proposalsLoading && !sharepointFiles.length && !proposalsError && (
              <div className="chatbot-logs-message">No files in the configured SharePoint folder.</div>
            )}
            <ul className="chatbot-proposals-list">
              {sharepointFiles.map((f) => (
                <li key={f.id || f.name} className={f.isFolder ? 'folder' : 'file'}>
                  {f.isFolder ? <FolderOpen size={16} className="icon-folder" /> : <FileText size={16} className="icon-file" />}
                  <span className="proposal-name">{f.name}</span>
                  {!f.isFolder && f.size != null && (
                    <span className="proposal-size">{(Number(f.size) / 1024).toFixed(1)} KB</span>
                  )}
                  {f.lastModifiedDateTime && (
                    <span className="proposal-date">
                      {new Date(f.lastModifiedDateTime).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
                    </span>
                  )}
                  {f.webUrl && (
                    <a
                      href={f.webUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="proposal-link"
                      title="Open in SharePoint"
                    >
                      <ExternalLink size={14} />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Upload File</h2>
            <p>Upload PDF, DOCX, TXT, or JSON files to index</p>
            <label className="upload-option">
              <input
                type="checkbox"
                checked={uploadReplaceStore}
                onChange={(e) => setUploadReplaceStore(e.target.checked)}
              />
              Replace existing index (erase old chunks)
            </label>
            <input
              type="file"
              accept=".pdf,.docx,.doc,.txt,.json"
              onChange={(e) => setUploadFile(e.target.files[0])}
            />
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowUploadModal(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleFileUpload}
                disabled={!uploadFile || uploading}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Crawl Modal */}
      {showCrawlModal && (
        <div className="modal-overlay" onClick={() => setShowCrawlModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Crawl Website</h2>
            <p>Enter a website URL to crawl and index</p>
            <input
              type="url"
              value={crawlUrl}
              onChange={(e) => setCrawlUrl(e.target.value)}
              placeholder="https://example.com"
            />
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowCrawlModal(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleCrawl}
                disabled={!crawlUrl.trim() || crawling}
              >
                {crawling ? 'Crawling...' : 'Crawl'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatbotPage;
